import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { PlatformEnum } from '@panamax/app-state';
import { isDateGreater } from '@shared/helpers/calendar.helpers';
import { createDateToolTip } from '@shared/helpers/chart.helpers';
import { Chart } from 'chart.js/auto';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import {
  BehaviorSubject,
  combineLatest,
  map,
  pairwise,
  Subscription,
} from 'rxjs';

@Component({
  selector: 'app-graph-tile',
  templateUrl: './graph-tile.component.html',
  styleUrls: ['./graph-tile.component.scss'],
})
export class GraphTileComponent implements AfterViewInit, OnDestroy {
  @ViewChild('FoodCostGraph') foodCostGraph: ElementRef<HTMLCanvasElement>;
  @Input() graphData$: BehaviorSubject<any>;
  // {x: date, y: number}[]
  @Input() platform: PlatformEnum;
  @Input() foodCostRange$: BehaviorSubject<any>;
  // {start: number, end: number}

  graphSub: Subscription;
  graphDataForChart = [];
  foodCostRange = undefined;
  public chart: any;
  public showGraph = false;
  public reportsReady = false;
  platformRef = PlatformEnum;
  defaultLabels = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];

  yGraph = {
    min: 0,
    max: 100,
    grid: {
      display: false,
    },
    afterTickToLabelConversion: function (q) {
      for (var tick in q.ticks) {
        q.ticks[tick].label += '%';
      }
    },
  };

  defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
    },
  };

  advancedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: createDateToolTip,
      },
    },
  };

  dataSetDefaults = {
    borderWidth: 2,
    tension: 0.05,
  };

  constructor() {}

  ngOnDestroy(): void {
    if (!!this.chart) this.chart.destroy();

    if (this.graphSub) {
      this.graphSub.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.setupGraphSub();
    if (this.foodCostGraph) {
      this.createChart();
    }
  }

  setupGraphSub = () => {
    this.graphSub = combineLatest([this.graphData$, this.foodCostRange$])
      .pipe(
        map(values => {
          return { data: values[0], range: values[1] };
        }),
        pairwise(),
        map(([pv, cv]) => {
          return {
            graphData: cv.data,
            range: cv.range,
            needUpdate: this.dataChanged(pv, cv),
          };
        }),
      )
      .subscribe(data => {
        if (data.needUpdate) {
          this.graphDataForChart = data.graphData;
          this.foodCostRange = data.range;
          this.refreshChart();
        }
      });
  };
  dataChanged = (previous, current) => {
    let dataChanged =
      !!current?.data && previous?.data?.length !== current.data.length;
    // if current data is defined, and previous length === current length
    if (!dataChanged) {
      current.data.forEach((element, index) => {
        if (
          element?.x !== previous?.data[index]?.x ||
          element?.y !== previous?.data[index]?.y
        ) {
          dataChanged = true;
        }
      });
    }
    const rangeChanged =
      !!current?.range &&
      (previous.range?.start !== current.range.start ||
        previous.range?.end !== current.range.end);
    return dataChanged || rangeChanged;
  };

  refreshChart = () => {
    if (!!this.chart) this.chart.destroy();

    this.createChart();
  };

  isDataValid = () => {
    if (!this.graphDataForChart) {
      this.showGraph = false;
      this.reportsReady = false;
      return false;
    }

    this.reportsReady = this.graphDataForChart.length > 1;

    if (
      !this.foodCostRange ||
      this.foodCostRange?.start === undefined ||
      this.foodCostRange?.end === undefined
    ) {
      this.showGraph = false;
      return false;
    }

    if (this.graphDataForChart.length <= 1) {
      this.showGraph = false;
      return false;
    }

    this.showGraph = true;
    return true;
  };

  createChart = () => {
    if (!!this.chart) {
      this.chart.destroy();
    }
    this.chart = this.isDataValid()
      ? this.getModifiableChart()
      : this.getDefaultChart();
  };

  datesGreaterThan3MonthsOrDefault = (min: Date, max: string | number) => {
    const checkDate = new Date(min);
    const maxDate = new Date(max);
    checkDate.setMonth(min.getMonth() + 3);
    if (isDateGreater(checkDate, maxDate)) {
      return checkDate;
    }
    return maxDate;
  };

  getModifiableChart = () => {
    const minDate = new Date(this.graphDataForChart[0]?.x);
    const maxDate = this.datesGreaterThan3MonthsOrDefault(
      minDate,
      this.graphDataForChart[this.graphDataForChart.length - 1]?.x,
    );
    minDate.setDate(minDate.getDate() - 7);
    maxDate.setDate(maxDate.getDate() + 3);

    return new Chart(this.foodCostGraph.nativeElement, {
      type: 'line',
      data: {
        labels: this.defaultLabels,
        datasets: [
          {
            ...this.dataSetDefaults,
            borderColor: context => {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) {
                return;
              }
              return this.getGradient(ctx, chartArea);
            },
            data: this.graphData$.value,
            pointRadius: 2,
            pointHoverRadius: 4,
          },
        ],
      },
      options: {
        ...this.advancedOptions,
        scales: {
          x: {
            min: minDate.toDateString(),
            max: maxDate.toDateString(),
            grid: {
              display: true,
            },
            afterTickToLabelConversion: function (q) {
              for (var tick in q.ticks) {
                let tempLabel = q.ticks[tick].label.toString();
                if (tempLabel.includes(' ')) {
                  q.ticks[tick].label = tempLabel.split(' ');
                }
              }
            },
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: {
                month: 'MMM YY',
              },
            },
          },
          y: this.yGraph,
        },
      },
    });
  };

  getDefaultChart = () => {
    return new Chart(this.foodCostGraph.nativeElement, {
      type: 'line',
      data: {
        labels: this.defaultLabels,
        datasets: [
          {
            ...this.dataSetDefaults,
            backgroundColor: 'rgba(75,192,192,0.4)',
            data: [],
          },
        ],
      },
      options: {
        ...this.defaultOptions,
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: this.yGraph,
        },
      },
    });
  };

  getGradient = (ctx, chartArea) => {
    let width, height, gradient;
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!gradient || width !== chartWidth || height !== chartHeight) {
      width = chartWidth;
      height = chartHeight;
      gradient = ctx.createLinearGradient(
        0,
        chartArea.bottom,
        0,
        chartArea.top,
      );
      const onTrackLowerLimit = this.foodCostRange?.start / 100;
      const onTrackUpperLimit = this.foodCostRange?.end / 100;
      const belowTarget = onTrackLowerLimit - 0.0001;
      const aboveTarget = onTrackUpperLimit + 0.0001;
      gradient.addColorStop(
        belowTarget < 0 ? 0 : belowTarget,
        'rgba(47, 47, 47, 1)',
      );
      gradient.addColorStop(onTrackLowerLimit, 'rgba(86, 130, 31, 1)');
      gradient.addColorStop(onTrackUpperLimit, 'rgba(86, 130, 31, 1)');
      gradient.addColorStop(
        aboveTarget > 1 ? 1 : aboveTarget,
        'rgba(207, 69, 32, 1)',
      );
    }

    return gradient;
  };
}
